import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Table, Spinner, ProgressBar, Badge, Tabs, Tab } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const BULAN_OPTIONS = [
  { val: 1, label: '01 - Januari' },
  { val: 2, label: '02 - Februari' },
  { val: 3, label: '03 - Maret' },
  { val: 4, label: '04 - April' },
  { val: 5, label: '05 - Mei' },
  { val: 6, label: '06 - Juni' },
  { val: 7, label: '07 - Juli' },
  { val: 8, label: '08 - Agustus' },
  { val: 9, label: '09 - September' },
  { val: 10, label: '10 - Oktober' },
  { val: 11, label: '11 - November' },
  { val: 12, label: '12 - Desember' },
];

const KATEGORI_OPTIONS = [
  { val: 'all', label: 'Semua Kategori', color: 'primary' },
  { val: '001', label: 'Narkotika (001)', color: 'danger' },
  { val: '002', label: 'Psikotropika (002)', color: 'warning' },
  { val: '003', label: 'Prekursor (003)', color: 'info' },
];

const fmtNum = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const LaporanApotek = () => {
  const navigate = useNavigate();

  // State Parameter
  const now = new Date();
  const [lokasi, setLokasi] = useState('00001');
  const [lokasiInfo, setLokasiInfo] = useState({
    kdsp: '00001',
    nmsp: 'RS Mary Cileungsi | Farmasi Rawat Jalan',
    typeclient: 'Pelayanan',
    apoteker: 'apt. Lastry Situmorang, S.Farm',
    sik: '-'
  });
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [kategori, setKategori] = useState('all');
  const [jenisLap, setJenisLap] = useState('rekap'); // 'rekap' | 'rincian'
  const [hanyaMutasi, setHanyaMutasi] = useState(true);

  // State Data & UI
  const [isLoading, setIsLoading] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rekapData, setRekapData] = useState([]);
  const [rincianData, setRincianData] = useState([]);
  const [summary, setSummary] = useState({
    total_rekap_items: 0,
    total_rincian_items: 0,
    total_narkotika: 0,
    total_psikotropika: 0,
    total_prekursor: 0,
    total_beli: 0,
    total_jual: 0
  });


  // State Tab Klasifikasi
  const [klasifikasiList, setKlasifikasiList] = useState([]);
  const [selectedKlas, setSelectedKlas] = useState('');
  const [klasData, setKlasData] = useState([]);
  const [isKlasLoading, setIsKlasLoading] = useState(false);
  const [klasSearchTerm, setKlasSearchTerm] = useState('');

  // Modal Lokasi
  const [showModalLokasi, setShowModalLokasi] = useState(false);
  const [lokasiList, setLokasiList] = useState([]);
  const [lokasiSearch, setLokasiSearch] = useState('');
  const [isSearchingLokasi, setIsSearchingLokasi] = useState(false);

  // Modal PDF Viewer (In-App Preview Langsung)
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const iframePdfRef = useRef(null);

  // Live Clock & User
  const [time, setTime] = useState({ date: '', clock: '' });
  const [username, setUsername] = useState('Apoteker');

  useEffect(() => {
    try {
      const u = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (u) {
        const parsed = JSON.parse(u);
        setUsername(parsed.username || parsed.nama || 'Apoteker');
      }
    } catch {
      // fallback
    }

    const updateClock = () => {
      const d = new Date();
      setTime({
        date: d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        clock: d.toLocaleTimeString('id-ID') + ' WIB'
      });
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch daftar lokasi unit
  const fetchLokasiList = useCallback(async (keyword = '') => {
    try {
      setIsSearchingLokasi(true);
      const res = await fetch(`${getApiUrl()}/laporan_apotek_lokasi?q=${encodeURIComponent(keyword)}`);
      const json = await res.json();
      if (json.status === 1) {
        setLokasiList(json.data);
      }
    } catch (err) {
      console.error('Gagal mengambil lokasi:', err);
    } finally {
      setIsSearchingLokasi(false);
    }
  }, []);


  const fetchKlasifikasiList = useCallback(async () => {
    try {
      const res = await fetch(`${getApiUrl()}/laporan_apotek_klasifikasi_list`);
      const json = await res.json();
      if (json.status === 1) {
        setKlasifikasiList(json.data);
        if(json.data.length > 0) setSelectedKlas(json.data[0].kd_klas);
      }
    } catch (err) {
      console.error('Gagal mengambil klasifikasi:', err);
    }
  }, []);

  useEffect(() => {
    fetchLokasiList();
    fetchKlasifikasiList();
  }, [fetchLokasiList, fetchKlasifikasiList]);


  // Handle Pilih Lokasi
  const handleSelectLokasi = (item) => {
    setLokasi(item.kdsp);
    setLokasiInfo(item);
    setShowModalLokasi(false);
  };

  // Proses Tarik Data Laporan
  const handleProses = async () => {
    setIsLoading(true);
    setProgressVal(15);
    setProgressMsg('Menghubungkan ke server database apotek...');

    try {
      setTimeout(() => {
        setProgressVal(45);
        setProgressMsg('Memproses mutasi data penerimaan & pengeluaran resep...');
      }, 300);

      const url = `${getApiUrl()}/laporan_apotek_data?lokasi=${lokasi}&bulan=${bulan}&tahun=${tahun}&kategori=${kategori}&hanya_mutasi=${hanyaMutasi ? '1' : '0'}`;
      const res = await fetch(url);
      const json = await res.json();

      setProgressVal(85);
      setProgressMsg('Menyusun data rekapitulasi & rincian...');

      if (json.status === 1) {
        setRekapData(json.rekap || []);
        setRincianData(json.rincian || []);
        if (json.client) {
          setLokasiInfo(json.client);
        }
        if (json.summary) {
          setSummary(json.summary);
        }
        setTimeout(() => {
          setProgressVal(100);
          setProgressMsg('Data berhasil dimuat.');
          setTimeout(() => setIsLoading(false), 250);
        }, 200);
      } else {
        alert(json.msg || 'Gagal memuat data laporan');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error proses laporan:', err);
      alert('Terjadi kesalahan saat memproses laporan: ' + err.message);
      setIsLoading(false);
    }
  };


  const handleProsesKlasifikasi = async () => {
    if(!selectedKlas) { alert('Pilih klasifikasi!'); return; }
    setIsKlasLoading(true);
    setProgressVal(20);
    setProgressMsg('Memuat data laporan per klasifikasi...');

    try {
      const url = `${getApiUrl()}/laporan_apotek_klasifikasi_data?lokasi=${lokasi}&bulan=${bulan}&tahun=${tahun}&klasifikasi=${selectedKlas}`;
      const res = await fetch(url);
      const json = await res.json();
      
      setProgressVal(80);
      
      if (json.status === 1) {
        setKlasData(json.data || []);
      } else {
        alert(json.msg || 'Gagal memuat data');
      }
      setTimeout(() => {
          setProgressVal(100);
          setProgressMsg('Data dimuat');
          setTimeout(() => setIsKlasLoading(false), 200);
      }, 200);
    } catch (err) {
      console.error(err);
      alert('Error: ' + err.message);
      setIsKlasLoading(false);
    }
  };

  const filteredKlasData = useMemo(() => {
    if (!klasSearchTerm) return klasData;
    const q = klasSearchTerm.toLowerCase();
    return klasData.filter((r) => 
       (r.nm_obat && r.nm_obat.toLowerCase().includes(q)) || 
       (r.kd_obat && r.kd_obat.toLowerCase().includes(q)) ||
       (r.no_nota && r.no_nota.toLowerCase().includes(q))
    );
  }, [klasData, klasSearchTerm]);

  const cetakKlasifikasiPdf = () => {
    if (!selectedKlas) { alert('Pilih klasifikasi terlebih dahulu!'); return; }
    const klasObj = klasifikasiList.find(k => k.kd_klas === selectedKlas);
    const nmKlas = klasObj ? klasObj.nm_klas : selectedKlas;
    const bulanObj = BULAN_OPTIONS.find((b) => b.val === bulan);
    const bulanLabel = bulanObj ? bulanObj.label.split(' - ')[1] : bulan;
    const title = `Laporan Penjualan Per Klasifikasi (${nmKlas}) - ${bulanLabel} ${tahun}`;
    const url = `${getApiUrl()}/laporan_apotek_klasifikasi_pdf?lokasi=${lokasi}&bulan=${bulan}&tahun=${tahun}&klasifikasi=${selectedKlas}&_t=${Date.now()}`;
    setPdfUrl(url);
    setPdfTitle(title);
    setShowModalPdf(true);
  };

  const exportKlasifikasiCsv = () => {
     if(!filteredKlasData.length) { alert('Tidak ada data'); return; }
     let csv = 'data:text/csv;charset=utf-8,﻿';
     csv += 'No,No Nota,Kode,Nama Produk,Satuan,Tanggal,Qty,Harga,Total\r\n';
     filteredKlasData.forEach(r => {
        csv += `"${r.no}","${r.no_nota}","${r.kd_obat}","${(r.nm_obat||'').replace(/"/g,'""')}","${r.satuan}","${r.tanggal}","${r.qty}","${r.harsat}","${r.total}"\r\n`;
        if(r.details && r.details.length > 0) {
            r.details.forEach(det => {
                csv += `,"","${det.kd_obat}","${(det.nm_obat||'').replace(/"/g,'""')}","${det.satuan}","","${det.qty}","",""\r\n`;
            });
        }
     });
     const encodedUri = encodeURI(csv);
     const link = document.createElement('a');
     link.setAttribute('href', encodedUri);
     link.setAttribute('download', `Laporan_Klasifikasi_${selectedKlas}_${lokasi}_${bulan}_${tahun}.csv`);
     document.body.appendChild(link);
     link.click();
     document.body.removeChild(link);
  };
  // Otomatis load data pertama kali saat halaman dibuka
  useEffect(() => {
    handleProses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter Data berdasarkan Search Term di Frontend
  const filteredRekap = useMemo(() => {
    if (!searchTerm) return rekapData;
    const q = searchTerm.toLowerCase();
    return rekapData.filter(
      (r) =>
        (r.nm_obat && r.nm_obat.toLowerCase().includes(q)) ||
        (r.kd_obat && r.kd_obat.toLowerCase().includes(q)) ||
        (r.nm_laporan && r.nm_laporan.toLowerCase().includes(q)) ||
        (r.klasifikasi && r.klasifikasi.toLowerCase().includes(q))
    );
  }, [rekapData, searchTerm]);

  const filteredRincian = useMemo(() => {
    if (!searchTerm) return rincianData;
    const q = searchTerm.toLowerCase();
    return rincianData.filter(
      (r) =>
        (r.nm_obat && r.nm_obat.toLowerCase().includes(q)) ||
        (r.kd_obat && r.kd_obat.toLowerCase().includes(q)) ||
        (r.no_nota && r.no_nota.toLowerCase().includes(q)) ||
        (r.nm_pasien && r.nm_pasien.toLowerCase().includes(q)) ||
        (r.nm_dokter && r.nm_dokter.toLowerCase().includes(q)) ||
        (r.nm_laporan && r.nm_laporan.toLowerCase().includes(q))
    );
  }, [rincianData, searchTerm]);

  // Aksi Cetak PDF Resmi (Langsung Ditampilkan In-App Modal Tanpa Buka Tab Baru)
  const handleCetakPdf = () => {
    const catMap = {
      '001': 'Narkotika',
      '002': 'Psikotropika',
      '003': 'Prekursor',
      'all': 'Narkotika, Psikotropika & Prekursor'
    };
    const catLabel = catMap[kategori] || 'Narkotika';
    const lapLabel = jenisLap === 'rekap' ? 'Rekapitulasi' : 'Rincian Penggunaan';
    const bulanObj = BULAN_OPTIONS.find((b) => b.val === bulan);
    const bulanLabel = bulanObj ? bulanObj.label.split(' - ')[1] : bulan;

    const title = `Laporan ${lapLabel} ${catLabel} - ${bulanLabel} ${tahun}`;
    const url = `${getApiUrl()}/laporan_apotek_pdf?lokasi=${lokasi}&bulan=${bulan}&tahun=${tahun}&kategori=${kategori}&jenis_lap=${jenisLap}&hanya_mutasi=${hanyaMutasi ? '1' : '0'}&_t=${Date.now()}`;

    setPdfUrl(url);
    setPdfTitle(title);
    setShowModalPdf(true);
  };

  const handlePrintIframe = () => {
    if (iframePdfRef.current && iframePdfRef.current.contentWindow) {
      try {
        iframePdfRef.current.contentWindow.focus();
        iframePdfRef.current.contentWindow.print();
      } catch {
        window.open(pdfUrl, '_blank');
      }
    } else {
      window.open(pdfUrl, '_blank');
    }
  };

  // Aksi Unduh File TXT Semicolon Format Gambas / SIPNAP
  const handleDownloadTxt = () => {
    const url = `${getApiUrl()}/laporan_apotek_txt?lokasi=${lokasi}&bulan=${bulan}&tahun=${tahun}&kategori=${kategori === 'all' ? '001' : kategori}&jenis_lap=${jenisLap}`;
    window.open(url, '_blank');
  };

  // Aksi Ekspor CSV / Excel
  const handleExportCsv = () => {
    const isRekap = jenisLap === 'rekap';
    const rows = isRekap ? filteredRekap : filteredRincian;
    if (!rows || rows.length === 0) {
      alert('Tidak ada data untuk diekspor!');
      return;
    }

    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF';
    if (isRekap) {
      csvContent += 'No,Kategori,Kode Obat,Nama Obat,Satuan,Stok Awal,Beli PBF,Sarana Masuk,Jual/Resep,Sarana Keluar,Pemusnahan,Stok Akhir,Klasifikasi\r\n';
      rows.forEach((r, idx) => {
        csvContent += `"${idx + 1}","${r.nm_laporan || ''}","${r.kd_obat || ''}","${(r.nm_obat || '').replace(/"/g, '""')}","${r.satuan || ''}","${r.stok_awal || 0}","${r.beli_pbf || 0}","${r.sarana_masuk || 0}","${r.jual_resep || 0}","${r.sarana_keluar || 0}","${r.pemusnahan || 0}","${r.stok_akhir || 0}","${r.klasifikasi || ''}"\r\n`;
      });
    } else {
      csvContent += 'No,Tanggal,Kategori,Kode Obat,Nama Obat,Satuan,Qty,No Nota,Nama Pasien,Alamat,Dokter,Klasifikasi\r\n';
      rows.forEach((r, idx) => {
        csvContent += `"${idx + 1}","${r.tanggal || ''}","${r.nm_laporan || ''}","${r.kd_obat || ''}","${(r.nm_obat || '').replace(/"/g, '""')}","${r.satuan || ''}","${r.qty || 0}","${r.no_nota || ''}","${(r.nm_pasien || '').replace(/"/g, '""')}","${(r.alamat_pasien || '').replace(/"/g, '""')}","${(r.nm_dokter || '').replace(/"/g, '""')}","${r.klasifikasi || ''}"\r\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Laporan_${isRekap ? 'Rekap' : 'Rinci'}_${lokasi}_${bulan}_${tahun}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const badgeCategoryColor = (kd) => {
    if (kd === '001') return 'danger';
    if (kd === '002') return 'warning';
    return 'info';
  };

  return (
    <div style={{ background: '#f1f5f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. TOP HEADER BAR */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          color: '#fff',
          padding: '10px 20px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              background: 'rgba(255,255,255,0.2)',
              width: 40,
              height: 40,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.3rem'
            }}
          >
            <i className="bi bi-file-earmark-medical-fill" />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ margin: 0, fontWeight: 800, fontSize: '1rem', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                Laporan Narkotika, Psikotropika & Prekursor
              </span>
              <Badge bg="light" text="dark" style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 7px' }}>
                SIMRS APOTEK
              </Badge>
            </div>
            <div style={{ opacity: 0.92, fontSize: '0.78rem', lineHeight: 1.3 }}>
              RS Mary Cileungsi Hijau &bull; Rekapitulasi & Rincian Penggunaan Obat Resmi SIPNAP
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right', fontSize: '0.78rem', lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700 }}>{time.clock} <span style={{ opacity: 0.85, fontWeight: 500 }}>&bull; {time.date}</span></div>
            <div style={{ opacity: 0.85, fontSize: '0.72rem' }}>User: <b>{username}</b></div>
          </div>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate('/home')}
            style={{ fontWeight: 700, borderRadius: 8, padding: '5px 12px', fontSize: '0.78rem' }}
          >
            <i className="bi bi-arrow-left me-1" /> Menu Utama
          </Button>
        </div>
      </div>

      
<div style={{ flex: 1, padding: '16px 20px' }}>
        <style>
          {`
            .custom-tabs .nav-link {
               font-weight: 600;
               color: #475569;
               border-radius: 8px 8px 0 0;
               padding: 10px 20px;
               font-size: 0.85rem;
            }
            .custom-tabs .nav-link.active {
               color: #0369a1;
               background-color: #fff;
               border-bottom-color: transparent;
            }
          `}
        </style>
        <Tabs id="laporan-apotek-tabs" defaultActiveKey="sipnap" className="mb-3 custom-tabs">
          <Tab eventKey="sipnap" title="Laporan Narkotika/Psikotropika (SIPNAP)">

        {/* 2. FILTER & CONTROL PANEL */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 10,
            padding: '14px 20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
            marginBottom: 14
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Lokasi Unit Farmasi */}
            <div className="col-12 col-md-4">
              <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Lokasi / Unit Farmasi
              </label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light fw-bold text-primary" style={{ minWidth: 60, fontSize: '0.80rem' }}>
                  {lokasi}
                </span>
                <input
                  type="text"
                  readOnly
                  className="form-control fw-semibold"
                  value={lokasiInfo.nmsp || 'Pilih Lokasi Farmasi...'}
                  style={{ background: '#f8fafc', cursor: 'pointer', fontSize: '0.80rem' }}
                  onClick={() => setShowModalLokasi(true)}
                  title="Klik untuk memilih unit farmasi"
                />
                <Button variant="primary" size="sm" onClick={() => setShowModalLokasi(true)}>
                  <i className="bi bi-search" />
                </Button>
              </div>
            </div>

            {/* Bulan Periode */}
            <div className="col-6 col-md-2">
              <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Bulan Periode
              </label>
              <select
                className="form-select form-select-sm fw-semibold"
                value={bulan}
                onChange={(e) => setBulan(parseInt(e.target.value))}
                style={{ fontSize: '0.80rem' }}
              >
                {BULAN_OPTIONS.map((b) => (
                  <option key={b.val} value={b.val}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tahun Periode */}
            <div className="col-6 col-md-2">
              <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Tahun
              </label>
              <input
                type="number"
                className="form-control form-control-sm fw-semibold"
                value={tahun}
                onChange={(e) => setTahun(parseInt(e.target.value) || now.getFullYear())}
                style={{ fontSize: '0.80rem' }}
                min="2015"
                max="2035"
              />
            </div>

            {/* Tombol Proses & Switch Mutasi */}
            <div className="col-12 col-md-4 d-flex align-items-end justify-content-between flex-wrap gap-2">
              <div className="form-check form-switch mb-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hanyaMutasiSwitch"
                  checked={hanyaMutasi}
                  onChange={(e) => setHanyaMutasi(e.target.checked)}
                />
                <label className="form-check-label fw-semibold text-secondary" htmlFor="hanyaMutasiSwitch" style={{ fontSize: '0.76rem' }}>
                  Hanya yang ada mutasi (Beli/Jual &gt; 0)
                </label>
              </div>

              <Button
                variant="primary"
                size="sm"
                disabled={isLoading}
                onClick={handleProses}
                className="d-flex align-items-center gap-1.5 px-3 py-1.5"
                style={{ fontWeight: 800, background: '#0284c7', borderColor: '#0369a1', borderRadius: 8, fontSize: '0.80rem' }}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-repeat" />}
                <span>PROSES LAPORAN</span>
              </Button>
            </div>
          </div>

          {/* Sub Row: Segmented Filters (Kategori & Jenis Laporan) */}
          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            {/* Toggle Jenis Laporan (Rekapitulasi vs Rincian) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b' }}>TIPE:</span>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${jenisLap === 'rekap' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                  onClick={() => setJenisLap('rekap')}
                  style={{ fontSize: '0.78rem', padding: '4px 12px' }}
                >
                  <i className="bi bi-table me-1" /> Rekapitulasi Mutasi
                </button>
                <button
                  type="button"
                  className={`btn ${jenisLap === 'rincian' ? 'btn-primary fw-bold' : 'btn-outline-secondary'}`}
                  onClick={() => setJenisLap('rincian')}
                  style={{ fontSize: '0.78rem', padding: '4px 12px' }}
                >
                  <i className="bi bi-list-check me-1" /> Rincian Penggunaan Resep
                </button>
              </div>
            </div>

            {/* Filter Kategori Obat */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#64748b' }}>KATEGORI:</span>
              {KATEGORI_OPTIONS.map((c) => {
                const isActive = kategori === c.val;
                return (
                  <Button
                    key={c.val}
                    variant={isActive ? c.color : 'outline-secondary'}
                    size="sm"
                    onClick={() => setKategori(c.val)}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: isActive ? 800 : 600,
                      borderRadius: 18,
                      padding: '3px 10px'
                    }}
                  >
                    {c.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Progress Bar Loading */}
          {isLoading && (
            <div style={{ marginTop: 10 }}>
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.74rem', fontWeight: 600, color: '#0284c7' }}>
                <span>{progressMsg}</span>
                <span>{progressVal}%</span>
              </div>
              <ProgressBar now={progressVal} animated variant="info" style={{ height: 6, borderRadius: 3 }} />
            </div>
          )}
        </div>

        {/* 3. KPI SUMMARY CARDS */}
        <div className="row g-3 mb-3">
          <div className="col-6 col-md-3">
            <div
              style={{
                background: '#ffffff',
                borderLeft: '4px solid #0284c7',
                borderRadius: 10,
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Total Item Obat Terdata
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                {jenisLap === 'rekap' ? filteredRekap.length : filteredRincian.length}
                <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>
                  {jenisLap === 'rekap' ? 'jenis obat' : 'transaksi'}
                </span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              style={{
                background: '#ffffff',
                borderLeft: '4px solid #10b981',
                borderRadius: 10,
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Total Penerimaan (Beli PBF)
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#059669', marginTop: 4 }}>
                {fmtNum(summary.total_beli)}
                <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>qty</span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              style={{
                background: '#ffffff',
                borderLeft: '4px solid #ef4444',
                borderRadius: 10,
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Total Penjualan (Resep)
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#dc2626', marginTop: 4 }}>
                {fmtNum(summary.total_jual)}
                <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>qty</span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              style={{
                background: '#ffffff',
                borderLeft: '4px solid #8b5cf6',
                borderRadius: 10,
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Saldo Net Mutasi
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#7c3aed', marginTop: 4 }}>
                {fmtNum(summary.total_beli - summary.total_jual)}
                <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>qty</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4. TABLE & SEARCH CARD */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 10,
            border: '1px solid #cbd5e1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Card Action Header */}
          <div
            style={{
              padding: '12px 18px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}
          >
            {/* Live Search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 350, flex: 1 }}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted" style={{ fontSize: '0.80rem' }} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder={jenisLap === 'rekap' ? 'Cari nama/kode obat...' : 'Cari obat/pasien/dokter...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: '0.80rem' }}
                />
                {searchTerm && (
                  <button className="btn btn-outline-secondary" type="button" onClick={() => setSearchTerm('')}>
                    <i className="bi bi-x" />
                  </button>
                )}
              </div>
            </div>

            {/* Export & Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Tombol Cetak PDF Resmi */}
              <Button
                variant="success"
                size="sm"
                onClick={handleCetakPdf}
                className="d-flex align-items-center gap-1.5"
                style={{ fontWeight: 700, padding: '5px 14px', fontSize: '0.80rem' }}
                title="Cetak PDF resmi landscape dengan kop RS dan tanda tangan apoteker"
              >
                <i className="bi bi-printer-fill" />
                <span>CETAK PDF</span>
              </Button>

              {/* Tombol Unduh File TXT Semicolon Gambas/SIPNAP */}
              <Button
                variant="info"
                size="sm"
                onClick={handleDownloadTxt}
                className="d-flex align-items-center gap-1.5 text-white"
                style={{ fontWeight: 700, padding: '5px 14px', fontSize: '0.80rem' }}
                title="Unduh file TXT format Gambas asli (pemisah titik koma ;)"
              >
                <i className="bi bi-file-earmark-text-fill" />
                <span>UNDUH TXT</span>
              </Button>

              {/* Tombol Ekspor CSV / Excel */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleExportCsv}
                className="d-flex align-items-center gap-1.5"
                style={{ fontWeight: 700, padding: '5px 14px', fontSize: '0.80rem', background: '#fff' }}
                title="Ekspor data tabel ke format CSV / Excel"
              >
                <i className="bi bi-file-earmark-spreadsheet text-success" />
                <span>EXCEL (CSV)</span>
              </Button>
            </div>
          </div>

          {/* Table Container */}
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 290px)' }}>
            {jenisLap === 'rekap' ? (
              /* TABEL REKAPITULASI (PERSIS GAMBAS) */
              <Table hover striped bordered responsive size="sm" className="m-0 align-middle" style={{ fontSize: '0.78rem' }}>
                <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10, fontSize: '0.75rem' }}>
                  <tr style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    <th style={{ width: 40, padding: '7px 8px' }}>NO</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>KODE</th>
                    <th style={{ width: 100, padding: '7px 8px' }}>KATEGORI</th>
                    <th style={{ minWidth: 200, textAlign: 'left', padding: '7px 8px' }}>NAMA OBAT / PRODUK</th>
                    <th style={{ width: 75, padding: '7px 8px' }}>SATUAN</th>
                    <th style={{ width: 75, padding: '7px 8px' }}>STOK AWAL</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>BELI PBF</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>SRN MASUK</th>
                    <th style={{ width: 90, padding: '7px 8px' }}>JUAL RESEP</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>SRN KELUAR</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>PEMUSNAHAN</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>STOK AKHIR</th>
                    <th style={{ minWidth: 120, textAlign: 'left', padding: '7px 8px' }}>KLASIFIKASI</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRekap.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-2 d-block mb-2 text-secondary" />
                        <div>Tidak ada data rekapitulasi untuk periode dan kriteria ini.</div>
                        <small className="text-secondary">Coba nonaktifkan toggle "Hanya yang ada mutasi" atau ubah filter periode.</small>
                      </td>
                    </tr>
                  ) : (
                    filteredRekap.map((r, idx) => (
                      <tr key={r.kd_obat || idx} style={{ verticalAlign: 'middle' }}>
                        <td className="text-center fw-semibold text-secondary" style={{ padding: '6px 8px' }}>{idx + 1}</td>
                        <td className="text-center font-monospace fw-bold text-primary" style={{ padding: '6px 8px' }}>{r.kd_obat}</td>
                        <td className="text-center" style={{ padding: '6px 8px' }}>
                          <Badge bg={badgeCategoryColor(r.kd_laporan)} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '3px 6px' }}>
                            {r.nm_laporan}
                          </Badge>
                        </td>
                        <td className="fw-semibold text-dark" style={{ padding: '6px 8px' }}>{r.nm_obat}</td>
                        <td className="text-center" style={{ padding: '6px 8px' }}>{r.satuan}</td>
                        <td className="text-end font-monospace" style={{ padding: '6px 8px' }}>{r.stok_awal}</td>
                        <td className="text-end font-monospace fw-bold text-success" style={{ padding: '6px 8px' }}>
                          {r.beli_pbf > 0 ? fmtNum(r.beli_pbf) : 0}
                        </td>
                        <td className="text-end font-monospace" style={{ padding: '6px 8px' }}>{r.sarana_masuk}</td>
                        <td className="text-end font-monospace fw-bold text-danger" style={{ padding: '6px 8px' }}>
                          {r.jual_resep > 0 ? fmtNum(r.jual_resep) : 0}
                        </td>
                        <td className="text-end font-monospace" style={{ padding: '6px 8px' }}>{r.sarana_keluar}</td>
                        <td className="text-end font-monospace" style={{ padding: '6px 8px' }}>{r.pemusnahan}</td>
                        <td className="text-end font-monospace fw-bold text-dark" style={{ padding: '6px 8px' }}>
                          {fmtNum(r.stok_akhir)}
                        </td>
                        <td className="text-muted" style={{ padding: '6px 8px' }}>{r.klasifikasi || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            ) : (
              /* TABEL RINCIAN PENGGUNAAN (PERSIS GAMBAS) */
              <Table hover striped bordered responsive size="sm" className="m-0 align-middle" style={{ fontSize: '0.78rem' }}>
                <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10, fontSize: '0.75rem' }}>
                  <tr style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    <th style={{ width: 40, padding: '7px 8px' }}>NO</th>
                    <th style={{ width: 85, padding: '7px 8px' }}>TANGGAL</th>
                    <th style={{ width: 100, padding: '7px 8px' }}>KATEGORI</th>
                    <th style={{ minWidth: 180, textAlign: 'left', padding: '7px 8px' }}>NAMA OBAT / PRODUK</th>
                    <th style={{ width: 75, padding: '7px 8px' }}>SATUAN</th>
                    <th style={{ width: 70, padding: '7px 8px' }}>JUMLAH</th>
                    <th style={{ width: 130, padding: '7px 8px' }}>NO NOTA / RESEP</th>
                    <th style={{ minWidth: 160, textAlign: 'left', padding: '7px 8px' }}>NAMA PASIEN / SARANA</th>
                    <th style={{ minWidth: 140, textAlign: 'left', padding: '7px 8px' }}>NAMA DOKTER</th>
                    <th style={{ minWidth: 100, textAlign: 'left', padding: '7px 8px' }}>KLASIFIKASI</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRincian.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-2 d-block mb-2 text-secondary" />
                        <div>Tidak ada rincian penggunaan resep pada periode ini.</div>
                      </td>
                    </tr>
                  ) : (
                    filteredRincian.map((r, idx) => (
                      <tr key={idx} style={{ verticalAlign: 'middle' }}>
                        <td className="text-center fw-semibold text-secondary" style={{ padding: '6px 8px' }}>{idx + 1}</td>
                        <td className="text-center font-monospace" style={{ padding: '6px 8px' }}>{r.tanggal}</td>
                        <td className="text-center" style={{ padding: '6px 8px' }}>
                          <Badge bg={badgeCategoryColor(r.kd_laporan)} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '3px 6px' }}>
                            {r.nm_laporan}
                          </Badge>
                        </td>
                        <td className="fw-semibold text-dark" style={{ padding: '6px 8px' }}>{r.nm_obat}</td>
                        <td className="text-center" style={{ padding: '6px 8px' }}>{r.satuan}</td>
                        <td className="text-end font-monospace fw-bold text-danger" style={{ padding: '6px 8px' }}>{fmtNum(r.qty)}</td>
                        <td className="text-center font-monospace" style={{ fontSize: '0.72rem', padding: '6px 8px' }}>
                          {r.no_nota}
                        </td>
                        <td className="fw-semibold" style={{ padding: '6px 8px' }}>{r.nm_pasien || '-'}</td>
                        <td style={{ padding: '6px 8px' }}>{r.nm_dokter || '-'}</td>
                        <td className="text-muted" style={{ padding: '6px 8px' }}>{r.klasifikasi || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </div>

          {/* Footer Card Informasi Penanggung Jawab */}
          <div
            style={{
              padding: '10px 18px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              fontSize: '0.78rem',
              color: '#475569'
            }}
          >
            <div>
              Unit Layanan: <b>{lokasiInfo.nmsp}</b> &bull; Alamat: <span>{lokasiInfo.alamat}</span>
            </div>
            <div>
              Apoteker Penanggung Jawab: <b>{lokasiInfo.apoteker || 'apt. Lastry Situmorang, S.Farm'}</b>
              {lokasiInfo.sik && lokasiInfo.sik !== '-' && (
                <span className="ms-2 badge bg-light text-dark border font-monospace py-1">SIK: {lokasiInfo.sik}</span>
              )}
            </div>
          </div>
        </div>

          </Tab>
          <Tab eventKey="klasifikasi" title="Penjualan Per Klasifikasi">
            <div style={{ background: '#ffffff', borderRadius: 10, padding: '14px 20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)', marginBottom: 14 }}>
              <div className="row g-3 align-items-center">
                <div className="col-12 col-md-3">
                  <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>Lokasi / Unit Farmasi</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light fw-bold text-primary" style={{ minWidth: 60, fontSize: '0.80rem' }}>{lokasi}</span>
                    <input type="text" readOnly className="form-control fw-semibold" value={lokasiInfo.nmsp || 'Pilih...'} style={{ background: '#f8fafc', cursor: 'pointer', fontSize: '0.80rem' }} onClick={() => setShowModalLokasi(true)} />
                    <Button variant="primary" size="sm" onClick={() => setShowModalLokasi(true)}><i className="bi bi-search" /></Button>
                  </div>
                </div>
                <div className="col-6 col-md-2">
                  <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>Bulan</label>
                  <select className="form-select form-select-sm fw-semibold" value={bulan} onChange={(e) => setBulan(parseInt(e.target.value))} style={{ fontSize: '0.80rem' }}>
                    {BULAN_OPTIONS.map((b) => (<option key={b.val} value={b.val}>{b.label}</option>))}
                  </select>
                </div>
                <div className="col-6 col-md-1">
                  <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>Tahun</label>
                  <input type="number" className="form-control form-control-sm fw-semibold text-center" value={tahun} onChange={(e) => setTahun(parseInt(e.target.value))} style={{ fontSize: '0.80rem' }} />
                </div>
                <div className="col-12 col-md-4">
                  <label style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>Klasifikasi</label>
                  <select className="form-select form-select-sm fw-semibold" value={selectedKlas} onChange={(e) => setSelectedKlas(e.target.value)} style={{ fontSize: '0.80rem' }}>
                    {klasifikasiList.map((k) => (<option key={k.kd_klas} value={k.kd_klas}>{k.kd_klas} - {k.nm_klas}</option>))}
                  </select>
                </div>
                <div className="col-12 col-md-2 text-md-end" style={{ marginTop: 24 }}>
                  <Button variant="success" size="sm" onClick={handleProsesKlasifikasi} disabled={isKlasLoading} style={{ fontWeight: 700, borderRadius: 8, padding: '5px 16px', fontSize: '0.78rem', width: '100%' }}>
                    {isKlasLoading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Proses...</> : <><i className="bi bi-play-circle-fill me-1" /> Proses</>}
                  </Button>
                </div>
              </div>
              
              {/* Progress Bar Loading untuk Klasifikasi */}
              {isKlasLoading && (
                <div style={{ marginTop: 10 }}>
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.74rem', fontWeight: 600, color: '#059669' }}>
                    <span>{progressMsg}</span>
                    <span>{progressVal}%</span>
                  </div>
                  <ProgressBar now={progressVal} animated variant="success" style={{ height: 6, borderRadius: 3 }} />
                </div>
              )}
            </div>

            <div style={{ background: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                  <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.85rem' }}><i className="bi bi-table me-2 text-primary" /> Rincian Penjualan Klasifikasi</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div className="input-group input-group-sm" style={{ width: 220 }}>
                      <span className="input-group-text bg-white" style={{ borderRight: 'none', color: '#94a3b8' }}><i className="bi bi-search" /></span>
                      <input type="text" className="form-control border-start-0 ps-0" placeholder="Cari obat/nota..." value={klasSearchTerm} onChange={(e) => setKlasSearchTerm(e.target.value)} style={{ fontSize: '0.78rem' }} />
                    </div>
                    <Button variant="outline-success" size="sm" onClick={exportKlasifikasiCsv} style={{ fontSize: '0.74rem', fontWeight: 600 }}><i className="bi bi-file-earmark-excel me-1" /> Excel</Button>
                    <Button variant="outline-danger" size="sm" onClick={cetakKlasifikasiPdf} style={{ fontSize: '0.74rem', fontWeight: 600 }}><i className="bi bi-printer me-1" /> PDF</Button>
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <Table striped hover responsive className="mb-0" style={{ fontSize: '0.78rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <tr>
                        <th style={{ width: '40px', textAlign: 'center', padding: '7px 8px' }}>NO</th>
                        <th style={{ padding: '7px 8px' }}>NO NOTA</th>
                        <th style={{ padding: '7px 8px' }}>KODE</th>
                        <th style={{ padding: '7px 8px' }}>NAMA PRODUK</th>
                        <th style={{ padding: '7px 8px' }}>SATUAN</th>
                        <th style={{ padding: '7px 8px' }}>TANGGAL</th>
                        <th style={{ textAlign: 'center', padding: '7px 8px' }}>QTY</th>
                        <th style={{ textAlign: 'right', padding: '7px 8px' }}>@ HARGA</th>
                        <th style={{ textAlign: 'right', padding: '7px 8px' }}>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isKlasLoading ? (
                        <tr><td colSpan="9" className="text-center py-5 text-muted"><Spinner animation="border" variant="primary" className="mb-2"/><br/>Memuat Data...</td></tr>
                      ) : filteredKlasData.length === 0 ? (
                        <tr><td colSpan="9" className="text-center py-4 text-muted"><i className="bi bi-inbox fs-3 mb-2 d-block opacity-50" />Data tidak ditemukan untuk periode & klasifikasi ini.</td></tr>
                      ) : (
                        filteredKlasData.map((row, idx) => (
                          <React.Fragment key={'k_'+idx}>
                            <tr style={{ background: row.is_racik_header ? '#f1f8ff' : 'transparent', fontWeight: row.is_racik_header ? '700' : 'normal' }}>
                              <td style={{ textAlign: 'center', padding: '6px 8px' }}>{row.no}</td>
                              <td style={{ padding: '6px 8px' }}>{row.no_nota}</td>
                              <td style={{ padding: '6px 8px' }}>{row.kd_obat}</td>
                              <td style={{ padding: '6px 8px' }}>{row.nm_obat}</td>
                              <td style={{ padding: '6px 8px' }}>{row.satuan}</td>
                              <td style={{ padding: '6px 8px' }}>{row.tanggal}</td>
                              <td style={{ textAlign: 'center', padding: '6px 8px' }}>{fmtNum(row.qty)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtNum(row.harsat)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>{fmtNum(row.total)}</td>
                            </tr>
                            {row.is_racik_header && row.details && row.details.length > 0 && row.details.map((det, didx) => (
                              <tr key={'kd_'+idx+'_'+didx}>
                                <td style={{ padding: '4px 8px' }}></td>
                                <td style={{ padding: '4px 8px' }}></td>
                                <td style={{ padding: '4px 8px', color: '#64748b' }}>{det.kd_obat}</td>
                                <td style={{ padding: '4px 8px', paddingLeft: '20px', color: '#64748b' }}>- {det.nm_obat}</td>
                                <td style={{ padding: '4px 8px', color: '#64748b' }}>{det.satuan}</td>
                                <td style={{ padding: '4px 8px' }}></td>
                                <td style={{ textAlign: 'center', padding: '4px 8px', color: '#64748b' }}>{fmtNum(det.qty)}</td>
                                <td style={{ padding: '4px 8px' }}></td>
                                <td style={{ padding: '4px 8px' }}></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
            </div>
          </Tab>
        </Tabs>
      </div>
      {/* 5. MODAL PILIH LOKASI UNIT FARMASI */}

      <Modal show={showModalLokasi} onHide={() => setShowModalLokasi(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#0284c7', color: '#fff', padding: '12px 18px' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
            <i className="bi bi-geo-alt-fill me-2" />
            Pilih Lokasi Unit Farmasi / Apotek
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px 18px' }}>
          <div className="input-group input-group-sm mb-3">
            <span className="input-group-text bg-light">
              <i className="bi bi-search text-muted" style={{ fontSize: '0.80rem' }} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Cari kode atau nama unit farmasi / apotek..."
              value={lokasiSearch}
              onChange={(e) => {
                setLokasiSearch(e.target.value);
                fetchLokasiList(e.target.value);
              }}
              style={{ fontSize: '0.82rem' }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <Table hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.80rem' }}>
              <thead style={{ background: '#f1f5f9', fontSize: '0.75rem' }}>
                <tr>
                  <th style={{ width: 80, padding: '6px 8px' }}>KODE</th>
                  <th style={{ padding: '6px 8px' }}>NAMA UNIT FARMASI</th>
                  <th style={{ width: 100, padding: '6px 8px' }}>TIPE</th>
                  <th style={{ padding: '6px 8px' }}>APOTEKER</th>
                  <th style={{ width: 70, textAlign: 'center', padding: '6px 8px' }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {isSearchingLokasi ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" /> Memuat data lokasi...
                    </td>
                  </tr>
                ) : lokasiList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      Tidak ada data lokasi yang sesuai dengan pencarian.
                    </td>
                  </tr>
                ) : (
                  lokasiList.map((l) => (
                    <tr key={l.kdsp} style={{ cursor: 'pointer' }} onClick={() => handleSelectLokasi(l)}>
                      <td className="font-monospace fw-bold text-primary" style={{ padding: '6px 8px' }}>{l.kdsp}</td>
                      <td className="fw-semibold" style={{ padding: '6px 8px' }}>{l.nmsp}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <Badge bg={l.typeclient === 'Gudang' ? 'secondary' : 'primary'} style={{ fontSize: '0.70rem', padding: '3px 6px' }}>{l.typeclient}</Badge>
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.75rem', padding: '6px 8px' }}>{l.apoteker}</td>
                      <td className="text-center" style={{ padding: '6px 8px' }}>
                        <Button variant="primary" size="sm" style={{ padding: '2px 8px', fontSize: '0.75rem' }}>
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 18px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowModalLokasi(false)} style={{ fontSize: '0.80rem', padding: '4px 12px' }}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 6. MODAL IN-APP PDF VIEWER (LANGSUNG DILIHAT TANPA BUKA TAB BARU) */}
      <Modal
        show={showModalPdf}
        onHide={() => {
          setShowModalPdf(false);
          setPdfUrl('');
        }}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            color: '#fff',
            padding: '12px 18px',
            borderBottom: '2px solid #0284c7'
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(239, 68, 68, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="bi bi-file-earmark-pdf-fill text-danger fs-5" />
            </div>
            <div>
              <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 800, margin: 0, color: '#fff' }}>
                {pdfTitle || 'Preview Dokumen Laporan PDF'}
              </Modal.Title>
              <small style={{ opacity: 0.82, fontSize: '0.74rem' }}>
                Unit: {lokasiInfo.nmsp} &bull; Periode: {bulan}/{tahun}
              </small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '82vh', background: '#334155', position: 'relative' }}>
          {pdfUrl ? (
            <iframe
              ref={iframePdfRef}
              src={pdfUrl}
              title={pdfTitle}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                gap: 12
              }}
            >
              <Spinner animation="border" variant="info" />
              <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Menyiapkan dokumen PDF laporan apotek...</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            padding: '8px 18px',
            background: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
            <i className="bi bi-info-circle me-1" />
            Dokumen PDF ditampilkan langsung di dalam sistem. Anda dapat langsung melihat, mencetak, atau menyimpannya.
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handlePrintIframe}
              style={{ fontWeight: 700, fontSize: '0.78rem', padding: '4px 12px' }}
            >
              <i className="bi bi-printer me-1" /> Cetak Langsung
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => window.open(pdfUrl, '_blank')}
              style={{ fontWeight: 600, fontSize: '0.78rem', padding: '4px 12px' }}
              title="Buka dokumen di tab terpisah jika diinginkan"
            >
              <i className="bi bi-box-arrow-up-right me-1" /> Buka Tab Baru
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setShowModalPdf(false);
                setPdfUrl('');
              }}
              style={{ fontWeight: 700, fontSize: '0.78rem', padding: '4px 12px' }}
            >
              Tutup Preview
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LaporanApotek;
